import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import './IntegrationSettings.css';

interface Campo {
  key: string;
  label: string;
  tipo: string;
  obrigatorio: boolean;
}

interface Mapeamento {
  campoLocal: string;
  campoApi: string;
}

interface ConfigIntegracao {
  id?: number;
  tipo_integracao: string;
  nome: string;
  descricao: string;
  ativo: boolean;
  url_api: string;
  token: string;
  documentacao_url: string;
  sincronizacao_automatica: boolean;
  intervalo_sincronizacao: number;
  mapeamento_campos: Record<string, string>;
  status: string;
}

const IntegrationSettings: React.FC = () => {
  const navigate = useNavigate();

  // Estados da configuração
  const [config, setConfig] = useState<ConfigIntegracao>({
    tipo_integracao: 'cta_smart',
    nome: 'Integração CTA Smart',
    descricao: 'Integração automática com o sistema CTA Smart para importação de abastecimentos',
    ativo: true,
    url_api: 'https://ctasmart.com.br:8443',
    token: '',
    documentacao_url: 'https://docs.ctasmart.com.br/API/sync',
    sincronizacao_automatica: false,
    intervalo_sincronizacao: 60,
    mapeamento_campos: {},
    status: 'configurado'
  });

  // Estados de campos disponíveis
  const [camposApi, setCamposApi] = useState<Campo[]>([]);
  const [camposLocais, setCamposLocais] = useState<Campo[]>([]);
  const [mapeamentos, setMapeamentos] = useState<Mapeamento[]>([]);

  // Estados de UI
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isTesting, setIsTesting] = useState(false);
  const [configExists, setConfigExists] = useState(false);
  const [configId, setConfigId] = useState<number | null>(null);

  // Estados de drag and drop
  const [draggedField, setDraggedField] = useState<{tipo: 'api' | 'local', campo: Campo} | null>(null);
  const [pendingApiField, setPendingApiField] = useState<Campo | null>(null);
  const [pendingLocalField, setPendingLocalField] = useState<Campo | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setIsLoading(true);

      // Carregar configuração existente
      try {
        const configResponse: any = await apiService.get('INTEGRATION_BY_TYPE', { tipo: 'cta_smart' });
        if (configResponse.data?.sucesso && configResponse.data?.dados) {
          const configData = configResponse.data.dados;
          setConfig(configData);
          setConfigExists(true);
          setConfigId(configData.id);

          // Converter mapeamento_campos para array de mapeamentos
          if (configData.mapeamento_campos) {
            const mappings: Mapeamento[] = Object.entries(configData.mapeamento_campos).map(([local, api]) => ({
              campoLocal: local,
              campoApi: api as string
            }));
            setMapeamentos(mappings);
          }
        }
      } catch (error) {
        // Configuração não existe ainda
        console.log('Configuração não encontrada, criando nova');
      }

      // Carregar campos disponíveis
      const camposResponse: any = await apiService.get('INTEGRATION_AVAILABLE_FIELDS', { tipo: 'cta_smart' });
      if (camposResponse.data?.sucesso && camposResponse.data?.dados) {
        setCamposApi(camposResponse.data.dados.campos_api || []);
        setCamposLocais(camposResponse.data.dados.campos_locais || []);
      }

    } catch (error: any) {
      console.error('Erro ao carregar dados:', error);
      utils.showNotification('Erro ao carregar configurações', { type: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);

      // Converter mapeamentos para objeto
      const mapeamento_campos: Record<string, string> = {};
      mapeamentos.forEach(m => {
        mapeamento_campos[m.campoLocal] = m.campoApi;
      });

      const configData = {
        ...config,
        mapeamento_campos
      };

      let response: any;
      if (configExists && configId) {
        // Atualizar configuração existente
        response = await apiService.put('INTEGRATION_BY_ID', configData, { id: configId });
      } else {
        // Criar nova configuração
        response = await apiService.post('INTEGRATIONS', configData);
      }

      if (response.data?.sucesso) {
        utils.showNotification('Configuração salva com sucesso!', { type: 'success' });
        if (!configExists) {
          setConfigExists(true);
          setConfigId(response.data.dados.id);
        }
      } else {
        utils.showNotification(response.data?.mensagem || 'Erro ao salvar configuração', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao salvar:', error);
      const mensagem = error.response?.data?.mensagem || error.message || 'Erro ao salvar configuração';
      utils.showNotification(mensagem, { type: 'error' });
    } finally {
      setIsSaving(false);
    }
  };

  // Obter campos disponíveis (não mapeados)
  const getAvailableApiFields = (): Campo[] => {
    const mappedKeys = mapeamentos.map(m => m.campoApi);
    return camposApi.filter(campo => !mappedKeys.includes(campo.key));
  };

  const getAvailableLocalFields = (): Campo[] => {
    const mappedKeys = mapeamentos.map(m => m.campoLocal);
    return camposLocais.filter(campo => !mappedKeys.includes(campo.key));
  };

  const handleDragStart = (tipo: 'api' | 'local', campo: Campo) => {
    setDraggedField({ tipo, campo });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDragEnd = () => {
    setDraggedField(null);
    // Remover todas as classes drag-over
    document.querySelectorAll('.drag-over').forEach(el => {
      el.classList.remove('drag-over');
    });
  };

  const handleDropOnApiZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    if (!draggedField) return;

    if (draggedField.tipo === 'api') {
      // Soltar campo da API na zona da API
      setPendingApiField(draggedField.campo);

      // Se já existe um campo local pendente, criar mapeamento
      if (pendingLocalField) {
        createMapping(draggedField.campo.key, pendingLocalField.key);
        setPendingApiField(null);
        setPendingLocalField(null);
      }
    } else {
      utils.showNotification('Esta zona é apenas para campos da API CTA Smart', { type: 'warning' });
    }

    setDraggedField(null);
  };

  const handleDropOnLocalZone = (e: React.DragEvent) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');

    if (!draggedField) return;

    if (draggedField.tipo === 'local') {
      // Soltar campo local na zona local
      setPendingLocalField(draggedField.campo);

      // Se já existe um campo da API pendente, criar mapeamento
      if (pendingApiField) {
        createMapping(pendingApiField.key, draggedField.campo.key);
        setPendingApiField(null);
        setPendingLocalField(null);
      }
    } else {
      utils.showNotification('Esta zona é apenas para campos locais', { type: 'warning' });
    }

    setDraggedField(null);
  };

  const createMapping = (apiKey: string, localKey: string) => {
    const newMapping: Mapeamento = {
      campoApi: apiKey,
      campoLocal: localKey
    };
    setMapeamentos([...mapeamentos, newMapping]);
    utils.showNotification('Mapeamento criado com sucesso!', { type: 'success' });
  };

  const clearPendingFields = () => {
    setPendingApiField(null);
    setPendingLocalField(null);
  };

  const handleRemoveMapping = (index: number) => {
    const newMappings = mapeamentos.filter((_, i) => i !== index);
    setMapeamentos(newMappings);
  };

  const handleTestConnection = async () => {
    if (!configId) {
      utils.showNotification('Salve a configuração antes de testar a conexão', { type: 'warning' });
      return;
    }

    try {
      setIsTesting(true);
      const response: any = await apiService.post('INTEGRATION_TEST', {}, { id: configId });

      if (response.data?.sucesso) {
        utils.showNotification(response.data.mensagem || 'Conexão estabelecida com sucesso!', { type: 'success' });
      } else {
        utils.showNotification(response.data?.mensagem || 'Falha ao testar conexão', { type: 'error' });
      }
    } catch (error: any) {
      console.error('Erro ao testar conexão:', error);
      const mensagem = error.response?.data?.mensagem || error.message || 'Erro ao testar conexão com a API';
      utils.showNotification(mensagem, { type: 'error' });
    } finally {
      setIsTesting(false);
    }
  };

  const getCampoLabel = (key: string, campos: Campo[]): string => {
    const campo = campos.find(c => c.key === key);
    return campo ? campo.label : key;
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Configuração de Integrações</h1>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando configurações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Configuração de Integrações - CTA Smart</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          <button
            className="btn btn-secondary"
            onClick={() => navigate('/configuracoes')}
          >
            <i className="fas fa-arrow-left"></i>
            Voltar
          </button>
          <button
            className="btn btn-primary"
            onClick={handleSave}
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="loading-spinner"></span>
                Salvando...
              </>
            ) : (
              <>
                <i className="fas fa-save"></i>
                Salvar Configuração
              </>
            )}
          </button>
        </div>
      </div>

      <div className="page-content">
        {/* Configurações Gerais */}
        <div className="card">
          <div className="card-header">
            <h2>Configurações Gerais</h2>
          </div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nome">
                  Nome da Integração <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="nome"
                  className="form-control"
                  value={config.nome}
                  onChange={(e) => setConfig({ ...config, nome: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="status">Status</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={config.ativo}
                      onChange={(e) => setConfig({ ...config, ativo: e.target.checked })}
                    />
                    <span>Integração Ativa</span>
                  </label>
                </div>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="descricao">Descrição</label>
              <textarea
                id="descricao"
                className="form-control"
                rows={3}
                value={config.descricao}
                onChange={(e) => setConfig({ ...config, descricao: e.target.value })}
              ></textarea>
            </div>
          </div>
        </div>

        {/* Configurações de Conexão */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h2>Configurações de Conexão</h2>
          </div>
          <div className="card-body">
            <div className="form-group">
              <label htmlFor="url_api">
                URL da API <span className="required">*</span>
              </label>
              <input
                type="url"
                id="url_api"
                className="form-control"
                value={config.url_api}
                onChange={(e) => setConfig({ ...config, url_api: e.target.value })}
                placeholder="https://ctasmart.com.br:8443"
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="token">
                Token de Acesso <span className="required">*</span>
              </label>
              <input
                type="text"
                id="token"
                className="form-control"
                value={config.token}
                onChange={(e) => setConfig({ ...config, token: e.target.value })}
                placeholder="Digite o token fornecido pela CTA Smart"
                required
              />
              <small className="form-text">
                Token fornecido pela CTA Smart para autenticação na API
              </small>
            </div>

            <div className="form-group">
              <label htmlFor="documentacao_url">URL da Documentação</label>
              <input
                type="url"
                id="documentacao_url"
                className="form-control"
                value={config.documentacao_url}
                onChange={(e) => setConfig({ ...config, documentacao_url: e.target.value })}
                placeholder="https://docs.ctasmart.com.br/API/sync"
              />
              <small className="form-text">
                <a href={config.documentacao_url} target="_blank" rel="noopener noreferrer">
                  <i className="fas fa-external-link-alt"></i> Acessar Documentação
                </a>
              </small>
            </div>

            <div className="form-group" style={{ marginTop: '20px' }}>
              <button
                className="btn btn-secondary"
                onClick={handleTestConnection}
                disabled={!configId || isTesting}
                style={{ width: 'auto' }}
              >
                {isTesting ? (
                  <>
                    <span className="loading-spinner"></span>
                    Testando Conexão...
                  </>
                ) : (
                  <>
                    <i className="fas fa-plug"></i>
                    Testar Conexão
                  </>
                )}
              </button>
              <small className="form-text" style={{ marginTop: '8px', display: 'block' }}>
                {!configId && 'Salve a configuração antes de testar a conexão'}
                {configId && 'Verificar se a URL e o token estão configurados corretamente'}
              </small>
            </div>
          </div>
        </div>

        {/* Sincronização Automática */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h2>Sincronização Automática</h2>
          </div>
          <div className="card-body">
            <div className="form-group">
              <div className="checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={config.sincronizacao_automatica}
                    onChange={(e) => setConfig({ ...config, sincronizacao_automatica: e.target.checked })}
                  />
                  <span>Habilitar sincronização automática</span>
                </label>
              </div>
              <small className="form-text">
                Quando habilitado, o sistema importará automaticamente os dados da CTA Smart no intervalo configurado
              </small>
            </div>

            {config.sincronizacao_automatica && (
              <div className="form-group">
                <label htmlFor="intervalo_sincronizacao">
                  Intervalo de Sincronização (minutos)
                </label>
                <input
                  type="number"
                  id="intervalo_sincronizacao"
                  className="form-control"
                  min="5"
                  max="1440"
                  value={config.intervalo_sincronizacao}
                  onChange={(e) => setConfig({ ...config, intervalo_sincronizacao: parseInt(e.target.value) || 60 })}
                />
                <small className="form-text">
                  Intervalo entre 5 minutos e 24 horas (1440 minutos). Recomendado: 60 minutos
                </small>
              </div>
            )}
          </div>
        </div>

        {/* Mapeamento de Campos */}
        <div className="card" style={{ marginTop: '20px' }}>
          <div className="card-header">
            <h2>Mapeamento de Campos</h2>
            <p style={{ fontSize: '14px', color: '#7f8c8d', marginTop: '5px', marginBottom: 0 }}>
              Arraste os campos para a área central para criar mapeamentos. Os campos se juntarão como um imã!
            </p>
          </div>
          <div className="card-body">
            <div className="mapping-container">
              {/* Campos da API */}
              <div className="fields-column">
                <h3>
                  <i className="fas fa-cloud"></i>
                  Campos da API CTA Smart
                  <span className="field-count">({getAvailableApiFields().length})</span>
                </h3>
                <div className="fields-list">
                  {getAvailableApiFields().length === 0 ? (
                    <div className="empty-fields-message">
                      <i className="fas fa-check-circle"></i>
                      <p>Todos os campos foram mapeados!</p>
                    </div>
                  ) : (
                    getAvailableApiFields().map((campo) => (
                      <div
                        key={campo.key}
                        className={`field-item ${campo.obrigatorio ? 'required' : ''}`}
                        draggable
                        onDragStart={() => handleDragStart('api', campo)}
                        onDragEnd={handleDragEnd}
                      >
                        <i className="fas fa-grip-vertical drag-handle"></i>
                        <span className="field-label">{campo.label}</span>
                        <span className="field-type">{campo.tipo}</span>
                        {campo.obrigatorio && <span className="field-badge">Obrigatório</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Área de Mapeamento */}
              <div className="mapping-area">
                <div className="mapping-header">
                  <h3>
                    <i className="fas fa-link"></i>
                    Mapeamentos Ativos ({mapeamentos.length})
                  </h3>
                  {(pendingApiField || pendingLocalField) && (
                    <button
                      className="btn-clear-pending"
                      onClick={clearPendingFields}
                      title="Limpar campos pendentes"
                    >
                      <i className="fas fa-times"></i> Limpar Pendentes
                    </button>
                  )}
                </div>

                {/* Drop Zones */}
                <div className="drop-zones">
                  <div
                    className={`drop-zone drop-zone-api ${pendingApiField ? 'has-field' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropOnApiZone}
                  >
                    <div className="drop-zone-header">
                      <i className="fas fa-cloud"></i>
                      <span>Solte campo da API aqui</span>
                    </div>
                    {pendingApiField && (
                      <div className="pending-field api-field">
                        <i className="fas fa-clock"></i>
                        <div>
                          <strong>{pendingApiField.label}</strong>
                          <small>{pendingApiField.tipo}</small>
                        </div>
                        <button
                          className="btn-remove-pending"
                          onClick={() => setPendingApiField(null)}
                          title="Remover"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {!pendingApiField && (
                      <div className="drop-zone-placeholder">
                        <i className="fas fa-hand-point-up fa-2x"></i>
                        <p>Arraste um campo da API</p>
                      </div>
                    )}
                  </div>

                  <div className="drop-zones-connector">
                    <i className="fas fa-plus"></i>
                  </div>

                  <div
                    className={`drop-zone drop-zone-local ${pendingLocalField ? 'has-field' : ''}`}
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDropOnLocalZone}
                  >
                    <div className="drop-zone-header">
                      <i className="fas fa-database"></i>
                      <span>Solte campo local aqui</span>
                    </div>
                    {pendingLocalField && (
                      <div className="pending-field local-field">
                        <i className="fas fa-clock"></i>
                        <div>
                          <strong>{pendingLocalField.label}</strong>
                          <small>{pendingLocalField.tipo}</small>
                        </div>
                        <button
                          className="btn-remove-pending"
                          onClick={() => setPendingLocalField(null)}
                          title="Remover"
                        >
                          <i className="fas fa-times"></i>
                        </button>
                      </div>
                    )}
                    {!pendingLocalField && (
                      <div className="drop-zone-placeholder">
                        <i className="fas fa-hand-point-up fa-2x"></i>
                        <p>Arraste um campo local</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Lista de Mapeamentos */}
                <div className="mappings-list">
                  {mapeamentos.length === 0 ? (
                    <div className="empty-state">
                      <i className="fas fa-info-circle fa-2x"></i>
                      <p>Nenhum mapeamento criado</p>
                      <small>Arraste campos da API e campos locais para as áreas acima.<br/>Quando soltar os dois, eles se juntarão automaticamente!</small>
                    </div>
                  ) : (
                    mapeamentos.map((mapping, index) => (
                      <div key={index} className="mapping-item">
                        <div className="mapping-from">
                          <strong>API:</strong> {getCampoLabel(mapping.campoApi, camposApi)}
                        </div>
                        <div className="mapping-arrow">
                          <i className="fas fa-arrows-alt-h"></i>
                        </div>
                        <div className="mapping-to">
                          <strong>Local:</strong> {getCampoLabel(mapping.campoLocal, camposLocais)}
                        </div>
                        <button
                          className="btn-remove-mapping"
                          onClick={() => handleRemoveMapping(index)}
                          title="Remover mapeamento"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Campos Locais */}
              <div className="fields-column">
                <h3>
                  <i className="fas fa-database"></i>
                  Campos Locais (Banco de Dados)
                  <span className="field-count">({getAvailableLocalFields().length})</span>
                </h3>
                <div className="fields-list">
                  {getAvailableLocalFields().length === 0 ? (
                    <div className="empty-fields-message">
                      <i className="fas fa-check-circle"></i>
                      <p>Todos os campos foram mapeados!</p>
                    </div>
                  ) : (
                    getAvailableLocalFields().map((campo) => (
                      <div
                        key={campo.key}
                        className={`field-item ${campo.obrigatorio ? 'required' : ''}`}
                        draggable
                        onDragStart={() => handleDragStart('local', campo)}
                        onDragEnd={handleDragEnd}
                      >
                        <i className="fas fa-grip-vertical drag-handle"></i>
                        <span className="field-label">{campo.label}</span>
                        <span className="field-type">{campo.tipo}</span>
                        {campo.obrigatorio && <span className="field-badge">Obrigatório</span>}
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IntegrationSettings;
